import React, { createContext, useCallback, useState, useContext, useRef } from 'react';
import CustomerService from 'services/CustomerService';
import { AlertMessagesContext } from 'react-alert-messages';

export const CustomerContext = createContext();

function CustomerProvider({ children }) {
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [customers, setCustomers] = useState(null);
  const customersCacheRef = useRef(null);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await CustomerService.getCustomers();
      return data || [];
    } catch (error) {
      postAlertMessage?.({
        text: error.message || 'Failed to fetch customers',
        type: 'failed',
        timeout: 5000,
      });
      return [];
    }
  }, [postAlertMessage]);

  const handleSetCustomers = useCallback((data) => {
    if (typeof data === 'function') {
      setCustomers((prev) => {
        const next = data(prev);
        customersCacheRef.current = next;
        return next;
      });
    } else {
      customersCacheRef.current = data;
      setCustomers(data);
    }
  }, []);

  const getCustomers = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && customersCacheRef.current) {
        return customersCacheRef.current;
      }
      const _customers = await fetchCustomers();
      customersCacheRef.current = _customers;
      setCustomers(_customers);
      return _customers;
    },
    [fetchCustomers]
  );

  const localCustomerUpdate = useCallback(
    (customerId, updates) => {
      if (!customerId) return;
      handleSetCustomers((prev) => {
        const currentList = prev || customersCacheRef.current;
        if (!Array.isArray(currentList)) return prev;

        return currentList.map((c) => {
          if (String(c.id) === String(customerId)) {
            const patch = typeof updates === 'function' ? updates(c) : updates;
            return { ...c, ...patch };
          }
          return c;
        });
      });
    },
    [handleSetCustomers]
  );

  return (
    <CustomerContext.Provider
      value={{
        customers,
        getCustomers,
        setCustomers: handleSetCustomers,
        localCustomerUpdate,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export default CustomerProvider;
